import * as Yup from 'yup';
import { fixGoDate, fixGoSlice } from '../helpers/fix-go-types';
import type { VolumeDiscount } from './product-pricing';
import { ProductPricingSchema } from './product-pricing';
import { ProductStatSchema } from './product-stat';

// form validation rules
export const ProductSchema = Yup.object().shape({
  id: Yup.string().required(),
  store_id: Yup.string().required(),
  vendor_id: Yup.string().defined(),

  vendor_name: Yup.string().defined(),
  brand: Yup.string().defined(),
  description: Yup.string().defined(),
  department: Yup.string().defined(),
  item_code: Yup.string().defined(),
  suggested_item_code: Yup.string().defined(),
  // Default barcode (QTY=0) from POS. See pkg/lpos/product.go:loadBarcodes.
  barcode: Yup.string().defined(),
  barcodes: Yup.array()
    .of(
      Yup.object({
        barcode: Yup.string().required(),
        quantity: Yup.number().required(),
      })
    )
    .defined()
    .transform(fixGoSlice),
  last_cost: Yup.number().defined(),
  price: Yup.number().defined(),
  promo_price: Yup.number().nullable().optional(),
  type: Yup.string().defined(),
  size: Yup.string().defined(),
  size_ounces: Yup.number().defined(),
  case_qty: Yup.number().defined(),
  standard_qty: Yup.number().defined(),
  order_lot: Yup.string().defined(),
  inventory_qty: Yup.number().defined(),
  stats: ProductStatSchema.required(),
  min_qoh: Yup.number(),
  disabled: Yup.boolean().required(),
  auto_disabled: Yup.boolean(),
  disabled_toggled_at: Yup.date().nullable().transform(fixGoDate),
  disabled_until: Yup.date().nullable().transform(fixGoDate),
  last_received: Yup.date().nullable().transform(fixGoDate),
  last_sold: Yup.date().nullable().transform(fixGoDate),
  notes: Yup.string().defined(),
  label_ids: Yup.array().of(Yup.number().required()).defined().transform(fixGoSlice),

  // Populated by nightly reconciliation when the product's vendor integration
  // has volume pricing; empty string otherwise. The prefix ("a_" or "u_")
  // encodes provenance — see pkg/family in the backend. useFamilies strips
  // the prefix for grouping; empty values fall into a synthetic "unassigned"
  // bucket as a defensive fallback for products not yet reconciled.
  family_id: Yup.string().defined(),

  pricing: ProductPricingSchema.required(),

  updated_at: Yup.date().required(),
});

export type Product = Yup.InferType<typeof ProductSchema>;

export const saleStatDays = [1, 7, 14, 30, 60, 90, 120, 150, 180, 365] as const;
export type SaleStatDays = (typeof saleStatDays)[number];

export function saleStat(p: Product, days: SaleStatDays): number {
  return p.stats[`last_${days}`];
}

export function casesSold(product: Product, period: SaleStatDays): number {
  if (product.case_qty === 0) {
    return 0;
  }

  return saleStat(product, period) / product.case_qty;
}

export function caseDiscount(product: Product) {
  if (!product.pricing.future_cost || !product.pricing.current_cost) {
    return 0;
  }

  const savings = product.pricing.future_cost - product.pricing.current_cost;
  if (savings > -0.01 && savings < 0.01) {
    return 0;
  }

  return savings;
}

export function hasCaseDiscount(product: Product) {
  return caseDiscount(product) !== 0;
}

export function volumeDiscount(product: Product, cases: number) {
  const frontline = product.pricing.current_cost ?? 0;
  if (frontline === 0) {
    return 0;
  }

  const savings = frontline - tierPrice(product, cases);
  if (savings > -0.01 && savings < 0.01) {
    return 0;
  }

  return savings;
}

export function hasVolumeDiscount(product: Product) {
  if (!product.pricing.volume_discounts || !product.pricing.current_cost) {
    return false;
  }

  return product.pricing.volume_discounts.length > 0;
}

/**
 * Whether a product has any buyup-style pricing data — at least one case
 * discount or volume discount tier. Used to decide if the product participates
 * in the buyup view.
 */
export function hasPricingData(product: Product): boolean {
  return hasCaseDiscount(product) || hasVolumeDiscount(product);
}

/**
 * Gate for the order-view → buyup-view pivot link on the product cell.
 * Volume-pricing vendors get the link whenever the product has volume
 * tiers (those deals are always active). Case-discount vendors get it
 * only when next month's case cost is strictly higher than this month's,
 * i.e. this month is the buying moment — flat or falling costs can wait.
 */
export function isPivotEligible(product: Product, vendorHasVolume: boolean): boolean {
  if (vendorHasVolume) {
    return hasVolumeDiscount(product);
  }

  const { current_cost: curr, future_cost: next } = product.pricing;

  return curr != null && next != null && curr < next;
}

/**
 * Per-case price the product actually pays at `cases` ordered — the deepest
 * volume-discount tier whose `min_cases` threshold the order reaches, falling
 * back to `current_cost` (frontline) when no tier qualifies.
 */
export function tierPrice(product: Product, cases: number): number {
  const frontline = product.pricing.current_cost ?? 0;

  return findActiveTier(product, cases)?.cost ?? frontline;
}

function findActiveTier(
  product: Product,
  cases: number
): { min_cases: number; cost: number } | null {
  const tiers = product.pricing.volume_discounts;
  if (!tiers) {
    return null;
  }

  for (let i = tiers.length - 1; i >= 0; --i) {
    if (cases >= tiers[i].min_cases) {
      return tiers[i];
    }
  }

  return null;
}

// case_qty=0 fallback avoids divide-by-zero on legacy/partial product records.
export function inventoryCases(product: Product): number {
  return product.inventory_qty / (product.case_qty || 1);
}

/**
 * A product is "active" if it's stockable (not disabled / auto-disabled) and
 * has had any unit sales in the last 90 days. Used to filter inactive members
 * out of family-level rollups (DOH min, hide-empty-family) and to gate which
 * products appear in the buyup view.
 */
export function isActiveProduct(product: Product): boolean {
  return !product.disabled && !product.auto_disabled && saleStat(product, 90) > 0;
}

export function effectivePrice(product: Product): number {
  return product.promo_price ?? product.price;
}

// Price per individual unit (effectivePrice covers standard_qty units).
export function unitPrice(product: Product): number {
  return effectivePrice(product) / product.standard_qty;
}

// Gross margin per pack (one standard_qty of units).
export function packMargin(product: Product): number {
  return effectivePrice(product) - product.standard_qty * product.last_cost;
}

// Gross margin per individual unit.
export function unitMargin(product: Product): number {
  return packMargin(product) / product.standard_qty;
}

export function hasPromoPrice(product: Product): boolean {
  return product.promo_price != null;
}

// Kneedle: normalize savings-per-case and min-cases to [0,1], return the tier with
// the greatest distance above the chord (y_norm − x_norm). Falls back to the deepest
// tier when the curve is linear (no tier stands out above the chord).
export function bestValueTierIndex(tiers: VolumeDiscount[], frontlineCost: number): number {
  if (tiers.length <= 1) {
    return 0;
  }

  const savings = tiers.map((t) => frontlineCost - t.cost);
  const cases = tiers.map((t) => t.min_cases);

  const xMin = cases[0];
  const xMax = cases[tiers.length - 1];
  const yMin = savings[0];
  const yMax = savings[tiers.length - 1];

  if (xMax === xMin || yMax === yMin) {
    return tiers.length - 1;
  }

  let bestIdx = tiers.length - 1;
  let bestDist = 0;

  for (let i = 0; i < tiers.length; i++) {
    const xn = (cases[i] - xMin) / (xMax - xMin);
    const yn = (savings[i] - yMin) / (yMax - yMin);
    const dist = yn - xn;

    if (dist > bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }

  return bestIdx;
}
